import { Link } from 'react-router-dom';
import Icon, { type IconName } from '../components/Icon';
import useDocumentTitle from '../hooks/useDocumentTitle';

interface Feature {
  icon: IconName;
  title: string;
  description: string;
}

const FEATURES: Feature[] = [
  {
    icon: 'link',
    title: 'اتصال امن با OAuth رسمی',
    description:
      'حساب اینستاگرام خود را فقط با ورود رسمی Meta متصل می‌کنید؛ هیچ رمز عبوری ذخیره نمی‌شود.',
  },
  {
    icon: 'zap',
    title: 'پاسخ خودکار به کامنت',
    description:
      'به کامنت‌های پست‌ها به‌صورت خودکار پاسخ دهید؛ با فیلتر کلمات کلیدی و اولویت‌بندی اتومیشن‌ها.',
  },
  {
    icon: 'inbox',
    title: 'پاسخ خودکار به دایرکت',
    description: 'دایرکت‌های ورودی را بر اساس کلمات کلیدی شناسایی و پاسخ متنی خودکار ارسال کنید.',
  },
  {
    icon: 'dashboard',
    title: 'پاسخ به ریپلای استوری',
    description: 'وقتی کسی به استوری شما ریپلای می‌دهد، پاسخ دایرکت خودکار ارسال کنید.',
  },
  {
    icon: 'list',
    title: 'فلوی چندمرحله‌ای و توقف مکالمه',
    description:
      'مکالمات را در چند مرحله هدایت کنید و در هر لحظه با توقف مکالمه، تسلط انسانی را حفظ کنید.',
  },
  {
    icon: 'chart',
    title: 'صندوق ورودی و آنالیتیکس',
    description:
      'تمام مکالمات در یک صندوق ورودی یکپارچه؛ به‌همراه آمار اجراها، لاگ رویدادها و داشبورد تحلیلی.',
  },
];

const STEPS = [
  {
    number: '۱',
    title: 'حساب خود را متصل کنید',
    description: 'با ورود رسمی Meta، حساب اینستاگرام را به پنل وصل کنید.',
  },
  {
    number: '۲',
    title: 'اتومیشن بسازید',
    description: 'کامنت، دایرکت یا ریپلای استوری را انتخاب و متن پاسخ را تنظیم کنید.',
  },
  {
    number: '۳',
    title: 'نتایج را دنبال کنید',
    description: 'از صندوق ورودی، لاگ اجراها و آنالیتیکس، عملکرد را ببینید.',
  },
];

export default function LandingPage() {
  useDocumentTitle('صفحه اصلی');

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-bold text-white">
              ع
            </div>
            <span className="text-sm font-bold sm:text-base">پنل اتومیشن اینستاگرام</span>
          </div>
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              className="rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 sm:px-4"
            >
              ورود
            </Link>
            <Link
              to="/register"
              className="rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90 sm:px-4"
            >
              ثبت‌نام
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="px-4 pb-16 pt-16 sm:px-6 sm:pb-24 sm:pt-24">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
              فقط با API رسمی Meta
            </div>
            <h1 className="text-3xl font-bold leading-tight sm:text-4xl">
              به کامنت‌ها و دایرکت‌های اینستاگرام
              <br className="hidden sm:block" /> به‌صورت خودکار پاسخ دهید
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
              این پنل به کمک API رسمی Instagram Graph، پاسخ به کامنت‌ها، دایرکت‌ها و ریپلای استوری
              را خودکار می‌کند؛ بدون جمع‌آوری رمز عبور و بدون ابزارهای غیررسمی.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <Link
                to="/register"
                className="w-full rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 sm:w-auto"
              >
                شروع رایگان
              </Link>
              <Link
                to="/login"
                className="w-full rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 sm:w-auto"
              >
                ورود به پنل
              </Link>
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 bg-white px-4 py-16 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">امکانات پنل</h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm leading-7 text-slate-500">
              همه امکانات روی زیرساخت Cloudflare و صف‌های پیام ساخته شده‌اند تا پاسخ‌ها سریع و
              پایدار ارسال شوند.
            </p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-indigo-200 hover:shadow"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
                    <Icon name={feature.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 sm:text-base">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-500">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-center text-2xl font-bold sm:text-3xl">چطور کار می‌کند؟</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              {STEPS.map((step) => (
                <div key={step.number} className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-base font-bold text-white">
                    {step.number}
                  </div>
                  <h3 className="mt-4 text-sm font-bold sm:text-base">{step.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-500">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 bg-white px-4 py-16 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">همین حالا شروع کنید</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-slate-500">
              حساب بسازید، اتومیشن تعریف کنید و پاسخ‌های خودکار را در پنل دنبال کنید.
            </p>
            <Link
              to="/register"
              className="mt-8 inline-block rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 px-8 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            >
              ساخت حساب رایگان
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-6xl text-center">
          <p className="text-xs leading-6 text-slate-400">
            پنل اتومیشن اینستاگرام — فقط با استفاده از API رسمی Meta / Instagram Graph API
          </p>
        </div>
      </footer>
    </div>
  );
}
